/**
 * Hash Utilities
 *
 * Provides hashing functions for change detection.
 * Used to detect if Fact Pack has changed since last journey generation.
 */

/**
 * Simple hash function for objects
 * Uses JSON stringification + cyrb53 hash algorithm
 *
 * Note: This is for change detection, not cryptographic security.
 * If cryptographic hashing is needed, use Web Crypto API.
 */
export function hashObject(obj: unknown): string {
  const str = JSON.stringify(obj, Object.keys(obj as object).sort());
  return cyrb53(str).toString(36);
}

/**
 * cyrb53 hash function
 * A fast, high-quality 53-bit hash function
 * Source: https://stackoverflow.com/a/52171480
 */
function cyrb53(str: string, seed: number = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;

  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * Simple string hash
 */
export function hashString(str: string): string {
  return cyrb53(str).toString(36);
}

/**
 * Compare two objects by their hash
 */
export function objectsEqual(obj1: unknown, obj2: unknown): boolean {
  return hashObject(obj1) === hashObject(obj2);
}

/**
 * Create a hash for FactPack excluding volatile fields.
 * This ensures consistent hashing for cache comparison regardless of when it was generated.
 *
 * Excluded fields (these change on every call even with same user data):
 * - generatedAt (top-level timestamp)
 * - preComputedJourneyData.computedAt
 * - preComputedJourneyData.dates.currentDate
 * - preComputedJourneyData.dates.currentDateFormatted
 * - preComputedJourneyData.dates.daysUntilMove (changes daily)
 * - preComputedJourneyData.dates.monthsUntilMove (changes monthly)
 */
export function hashFactPackForCache(factPack: Record<string, unknown>): string {
  const preComputed = factPack.preComputedJourneyData as Record<string, unknown> | undefined;
  const dates = preComputed?.dates as Record<string, unknown> | undefined;

  // Create a copy without volatile fields
  const hashable = {
    ...factPack,
    generatedAt: undefined, // Exclude top-level timestamp
    preComputedJourneyData: preComputed
      ? {
          ...preComputed,
          computedAt: undefined, // Exclude nested timestamp
          dates: dates
            ? {
                ...dates,
                currentDate: undefined, // Changes every call
                currentDateFormatted: undefined, // Changes every call
                daysUntilMove: undefined, // Changes daily
                monthsUntilMove: undefined, // Changes monthly
              }
            : undefined,
        }
      : undefined,
  };
  return hashObject(hashable);
}
